/**
 * goToContent action.
 */
import Addon from '../../classes/addon';
import AdUtils from '@shared/utils/ads';
import AppEvent from '../../classes/appEvent';
import ChannelUtils from '../../utils/channels';
import Content from '../../types/enums/content';
import Corner from '../../classes/corner';
import EpgEvent from '../../classes/epgEvent';
import Event from '../../classes/event';
import FastEvent from '../../classes/fastEvent';
import IACorner from '@shared/classes/iacorner';
import MediaProduction from '../../classes/mediaProduction';
import Modals from '../../types/enums/modals';
import ParentalUtils from '@shared/utils/parental';
import Slider from '../../classes/slider';
import goToAddon from '../addons/goToAddon';
import goToCorner from '../corner/goToCorner';
import goToChannel from '../channels/goToChannel';
import goToChannelsGuide from '../channels/goToChannelsGuide';
import goToDetail from '../detail/goToDetail';
import goToIACorner from '../corner/goToIACorner';
import goToPlayer from '../player/goToPlayer';
import goToSectionExtended from '../section/goToSectionExtended';
import goToSeasonExtended from '../season/goToSeasonExtended';
import goToSeriesExtended from '../series/goToSeriesExtended';
import redirect from '@shared/actions/app/redirect';
import { setVideoAd, setImpressionsUrls } from '@shared/store/slices/ads.slice';
import { setModal, setModalParams } from '../../store/slices/modal.slice';
import { store } from '../../store/store';

/**
 * Handles navigation to different content types based on the item's type.
 * Routes to appropriate destinations such as addons, channels, corners, details, players, etc.
 *
 * @param {Addon | EpgEvent | Event | FastEvent | IACorner | MediaProduction | Slider} item - The content item to navigate to
 * @param {boolean} [forceRestart] - Whether to force restart playback for channel content
 * @param {boolean} [forcePlayback] - Whether to force playback for EPG events
 * @param {number} [railIndex] - Index of the rail for "View More" content type
 */
function accessContent(
  item:
    | Addon
    | EpgEvent
    | Event
    | FastEvent
    | IACorner
    | MediaProduction
    | Slider,
  forceRestart?: boolean,
  forcePlayback?: boolean,
  railIndex?: number
) {
  // Access content
  switch (item.type) {
    case Content.ADDON:
      goToAddon(item as Addon);
      break;

    case Content.BACK_DOOR:
      redirect(item);
      break;

    case Content.CHANNEL_GUIDE:
      goToChannelsGuide(item as EpgEvent);
      break;

    case Content.APP:
      const params = (item as Slider)?.ogImg
        ? { src: (item as Slider)?.ogImg, width: 1400 }
        : (item as AppEvent)?.photo
        ? { src: (item as AppEvent)?.photo, width: 800 }
        : { src: '' };
      store.dispatch(setModalParams(params));
      store.dispatch(setModal(Modals.ZOOM));
      break;

    case Content.CHANNEL:
      const channel = ((item as Slider)?.content || (item as EpgEvent | Slider))
        .channel;
      goToChannel({ channel, pop: false, restart: forceRestart });
      break;

    case Content.CORNER:
      goToCorner(
        (item as Slider)?.corner || (item as Corner)?.id || '',
        (item as Corner).title || ''
      );
      break;

    case Content.IA_CORNER:
      goToIACorner((item as IACorner)?.id || '');
      break;

    case Content.EPG_EVENT:
    case Content.EVENT:
    case Content.MEDIA_PRODUCTION:
    case Content.SEASON:
    case Content.SERIES:
    case Content.SHOW:
      // Go to Detail if content is included in a superior plan
      if (
        ChannelUtils.getUpgradablePlan(
          (item as Event).channel || (item as Slider).content?.channel,
          false,
          item.type as Content
        ) ||
        (item instanceof FastEvent && !item.channel)
      ) {
        goToDetail((item as Slider)?.content || (item as FastEvent));
      }

      // Go to Season
      else if (item?.type === Content.SEASON) {
        const { id, isRecord, seasonId, seasonNumber, streams } =
          item as MediaProduction;
        !streams || seasonId
          ? goToSeasonExtended(
              item instanceof MediaProduction
                ? { id, isRecord, seasonNumber }
                : { seasonId }
            )
          : goToDetail(item as MediaProduction);
      }

      // Go to Series
      else if (item?.type === Content.SERIES) {
        const { eventId } = item as FastEvent;
        eventId
          ? goToSeriesExtended(item.eventId || '')
          : goToDetail(item as MediaProduction);
      }

      // Go to Zapper in case the content is an Epg Event
      else if (item?.type === Content.EPG_EVENT) {
        const isCurrentEvent =
          ((item as Slider)?.content && item.isCurrentEvent()) ||
          item.isCurrentEvent();
        isCurrentEvent || (item as EpgEvent)?.mocked || forcePlayback
          ? goToChannel({
              channel: ((item as Slider)?.content || (item as EpgEvent))
                .channel,
              pop: false,
              restart: forceRestart,
            })
          : goToDetail((item as Slider)?.content || (item as EpgEvent));
      }

      // Go to Player if content is not an Epg Event or any series
      else if (
        [Content.MEDIA_PRODUCTION, Content.SHOW].includes(item?.type as Content)
      ) {
        const asset = ((item as Slider)?.content || item) as
          | FastEvent
          | MediaProduction;
        if (asset?.streams) {
          goToPlayer({
            asset: ((item as Slider)?.content || item) as FastEvent,
            forceRestart,
          });
        } else {
          goToDetail(item as MediaProduction);
        }
      }

      // Go to to Detail in any other situation
      else {
        goToDetail(item as EpgEvent);
      }
      break;
    case Content.IMAGE_ADS:
      (item as Slider).impressions.promoSelection.forEach((impression) => {
        AdUtils.sendImageImpression(impression);
      });
      store.dispatch(
        setImpressionsUrls((item as Slider).impressions.promoSelection)
      );
      return;
    case Content.VIDEO_ADS:
      if ((item as Slider).link)
        store.dispatch(setVideoAd((item as Slider).link || ''));
      break;
    case Content.VIEW_MORE:
      goToSectionExtended(railIndex);
      break;
  }
}

/**
 * Main entry point for navigating to content.
 * Checks parental controls before allowing access to the content.
 * If parental control is required, shows the PIN modal and executes the callback after validation.
 *
 * @param {Addon | EpgEvent | Event | FastEvent | IACorner | MediaProduction | Slider | null | undefined} item - The content item to navigate to
 * @param {boolean} [forceRestart] - Whether to force restart playback for channel content
 * @param {boolean} [forcePlayback] - Whether to force playback for EPG events
 * @param {number} [railIndex] - Index of the rail for "View More" content type
 */
function goToContent(
  item:
    | Addon
    | EpgEvent
    | Event
    | FastEvent
    | IACorner
    | MediaProduction
    | Slider
    | null
    | undefined,
  forceRestart?: boolean,
  forcePlayback?: boolean,
  railIndex?: number
) {
  if (!item) return;

  if (
    !ParentalUtils.checkParentalControl(
      item?.channel?.currentEvent as Event,
      () => {
        store.dispatch(
          setModalParams({
            callback: () => accessContent(item, forceRestart, forcePlayback),
          })
        );
        store.dispatch(setModal(Modals.PARENTAL_PIN_GENERIC));
      }
    )
  ) {
    accessContent(item, forceRestart, forcePlayback, railIndex);
  }
}

export default goToContent;
